
interface MockPaymentProfile {
  profileId: string;
  lastFourAccountNumber: string;
  accountType: 'Checking' | 'Savings';
  accountProfile: 'Personal' | 'Business';
  bankName?: string;
  isDefault: boolean;
  firstName?: string;
  lastName?: string;
  businessAccountName?: string;
  loanType?: string;
}

export const autopayBankAccountsMock: Record<string, MockPaymentProfile[]> = {
  'multiple-accounts': [
    {
      profileId: 'mock-chase-1',
      lastFourAccountNumber: '4567',
      accountType: 'Checking',
      accountProfile: 'Personal',
      bankName: 'Chase Bank',
      isDefault: true,
      firstName: 'John',
      lastName: 'Smith',
      loanType: '0',
    },
    {
      profileId: 'mock-boa-2',
      lastFourAccountNumber: '8901',
      accountType: 'Savings',
      accountProfile: 'Personal',
      bankName: 'Bank of America',
      isDefault: false,
      firstName: 'John',
      lastName: 'Smith',
      loanType: '0',
    },
    {
      profileId: 'mock-wells-3',
      lastFourAccountNumber: '2345',
      accountType: 'Checking',
      accountProfile: 'Business',
      bankName: 'Wells Fargo',
      isDefault: false,
      businessAccountName: 'Smith Enterprises LLC',
      loanType: '1',
    },
  ],

  'single-account': [
    {
      profileId: 'mock-chase-1',
      lastFourAccountNumber: '4567',
      accountType: 'Checking',
      accountProfile: 'Personal',
      bankName: 'Chase Bank',
      isDefault: true,
      firstName: 'John',
      lastName: 'Smith',
      loanType: '0',
    },
  ],

  'no-accounts': [],

  'business-accounts': [
    {
      profileId: 'mock-wells-1',
      lastFourAccountNumber: '1234',
      accountType: 'Checking',
      accountProfile: 'Business',
      bankName: 'Wells Fargo',
      isDefault: true,
      businessAccountName: 'Smith Enterprises LLC',
      loanType: '1',
    },
    {
      profileId: 'mock-boa-2',
      lastFourAccountNumber: '5678',
      accountType: 'Checking',
      accountProfile: 'Business',
      bankName: 'Bank of America',
      isDefault: false,
      businessAccountName: 'Smith Holdings Inc',
      loanType: '0',
    },
  ],
};

export const autopaySetupMockResponses = {
  success: {
    paymentId: 'mock-payment-123',
    confirmationNumber: 'CONF-2024-001234',
    isSuccess: true,
  },

  'account-error': {
    paymentId: '',
    confirmationNumber: '',
    isSuccess: false,
    errorMessage: 'Bank account validation failed. Please verify your account details.',
  },

  'not-eligible': {
    paymentId: '',
    confirmationNumber: '',
    isSuccess: false,
    errorMessage: 'This loan is not eligible for autopay at this time.',
  },

  error: {
    paymentId: '',
    confirmationNumber: '',
    isSuccess: false,
    errorMessage: 'An error occurred while setting up autopay. Please try again.',
  },
};

export const autopayCancelMockResponses = {
  success: {
    isSuccess: true,
  },
  error: {
    isSuccess: false,
    errorMessage: 'Failed to cancel autopay. Please try again or contact support.',
  },
};

export const autopayExistingPaymentsMock = {
  'active-monthly': [
    {
      paymentId: 'existing-autopay-1',
      loanNumber: '1234567890',
      confirmationNumber: 'CONF-2024-000999',
      paymentFrequency: 1, // Monthly
      totalDraftAmount: 2847.32,
      monthlyPaymentAmount: 2847.32,
      recPaymentStartDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
      paymentProfileId: 'mock-chase-1',
      paymentProfileDetails: {
        firstName: 'John',
        lastName: 'Smith',
        accountProfile: 'Personal',
        accountType: 'Checking',
        routingNumber: '021000021',
        accountNumber: '****4567',
      },
      paymentStatus: 'Pending',
      recPaymentSetUpDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      userName: 'John Smith',
      userEmail: 'john.smith@email.com',
    },
  ],

  'active-biweekly': [
    {
      paymentId: 'existing-autopay-2',
      loanNumber: '1234567890',
      confirmationNumber: 'CONF-2024-000888',
      paymentFrequency: 2, // Biweekly
      totalDraftAmount: 1423.66,
      monthlyPaymentAmount: 2847.32,
      recPaymentStartDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(), // 3 days from now
      paymentProfileId: 'mock-boa-2',
      paymentProfileDetails: {
        firstName: 'John',
        lastName: 'Smith',
        accountProfile: 'Personal',
        accountType: 'Savings',
        routingNumber: '026009593',
        accountNumber: '****8901',
      },
      paymentStatus: 'Pending',
      recPaymentSetUpDate: new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date().toISOString(),
      userName: 'John Smith',
      userEmail: 'john.smith@email.com',
    },
  ],

  'processing-stuck': [
    {
      paymentId: 'existing-autopay-stuck',
      loanNumber: '1234567890',
      confirmationNumber: 'CONF-2024-001234',
      paymentFrequency: 1, // Monthly
      totalDraftAmount: 2847.32,
      monthlyPaymentAmount: 2847.32,
      recPaymentStartDate: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString(), // yesterday
      paymentProfileId: 'mock-chase-1',
      paymentProfileDetails: {
        firstName: 'John',
        lastName: 'Smith',
        accountProfile: 'Personal',
        accountType: 'Checking',
        routingNumber: '021000021',
        accountNumber: '****4567',
      },
      paymentStatus: 'Pending',
      paymentCancellationCutoffDate: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(), // 2 days ago (past cutoff)
      recPaymentSetUpDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
      userName: 'John Smith',
      userEmail: 'john.smith@email.com',
    },
  ],

  'no-autopay': [],
};

export const autopayDeletedPaymentsMock: Record<
  string,
  Array<{
    paymentId: string;
    loanNumber: string;
    paymentFrequency: 0 | 1 | 2;
    paymentStatus: string;
    updatedAt: string;
    deletedReason?: string;
  }>
> = {
  'no-deleted': [],

  'deleted-today': [
    {
      paymentId: 'deleted-autopay-1',
      loanNumber: '1234567890',
      paymentFrequency: 1, // Monthly autopay
      paymentStatus: 'Delete',
      updatedAt: new Date().toISOString(), // Today
      deletedReason: 'User requested cancellation',
    },
  ],

  'deleted-yesterday': [
    {
      paymentId: 'deleted-autopay-2',
      loanNumber: '1234567890',
      paymentFrequency: 1,
      paymentStatus: 'Delete',
      updatedAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Yesterday
      deletedReason: 'User requested cancellation',
    },
  ],
};
